import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Table,
  Box,
  Button,
  StatusIndicator,
  Alert,
  Badge,
  Modal,
  FormField,
  Textarea,
  Input,
  Checkbox,
  Select,
  ExpandableSection,
  Link,
  Pagination,
  Grid,
} from '@cloudscape-design/components';
import { getDataService } from '../services/dataServiceFactory';
import { filterVisibleMappings } from '../services/mappingVisibility';
import { getCleanUsername, getUserAlias } from '../auth/midway';

const MappingReviewTable = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mappingsData, setMappingsData] = useState([]);
  const [groupedMappings, setGroupedMappings] = useState([]);
  const [filteredMappings, setFilteredMappings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [itemsPerPage] = useState(100);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [currentMapping, setCurrentMapping] = useState(null);
  const [reviewDecision, setReviewDecision] = useState('');
  const [justification, setJustification] = useState('');
  const [showFinalSubmissionModal, setShowFinalSubmissionModal] = useState(false);
  const [finalJustification, setFinalJustification] = useState('');
  const [finalDecision, setFinalDecision] = useState('');
  const [isEdgeCaseMapping, setIsEdgeCaseMapping] = useState(false);
  const [controlDetails, setControlDetails] = useState(new Map());
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [currentAwsControl, setCurrentAwsControl] = useState(null);
  const [availableFrameworks, setAvailableFrameworks] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState(null);
  const [availableControls, setAvailableControls] = useState([]);
  const [selectedControl, setSelectedControl] = useState(null);
  const [suggestionJustification, setSuggestionJustification] = useState('');
  const [frameworkFilter, setFrameworkFilter] = useState(null);

  // Load mappings on mount
  useEffect(() => {
    loadMappings();
    loadAvailableFrameworks();
    
    // Handle navigation state filters from Dashboard
    if (location.state) {
      const { statusFilter, awsControlFilter } = location.state;
      
      // Apply AWS Control filter if provided
      if (awsControlFilter) {
        setSearchTerm(awsControlFilter);
      }
      
      // Note: Status filtering will be applied after data loads
      // We'll store it in a ref or state to apply after grouping
    }
  }, []);

  // Group mappings by AWS Control when data changes
  useEffect(() => {
    if (mappingsData.length > 0) {
      let grouped = groupMappingsByAwsControl(mappingsData);
      
      // Apply status filter from navigation state if provided
      if (location.state?.statusFilter) {
        const statusFilter = location.state.statusFilter;
        grouped = grouped
          .map(parent => {
            const filteredChildren = parent.children.filter(child => {
              const status = getMappingStatus(child);
              return status === statusFilter;
            });
            
            if (filteredChildren.length > 0) {
              return {
                ...parent,
                children: filteredChildren,
              };
            }
            return null;
          })
          .filter(Boolean);
        
        // Auto-expand filtered results
        const expandedKeys = new Set(grouped.map(g => g.awsControlKey));
        setExpandedRows(expandedKeys);
      }
      
      setGroupedMappings(grouped);
      setFilteredMappings(grouped);
    }
  }, [mappingsData, location.state]);

  // Filter mappings when search term or framework filter changes
  useEffect(() => {
    const term = searchTerm.trim().toLowerCase();
    const frameworkKey = frameworkFilter?.value && frameworkFilter.value !== 'all'
      ? frameworkFilter.value
      : null;

    if (!term && !frameworkKey) {
      setFilteredMappings(groupedMappings);
      return;
    }

    const matchesFramework = (child) => {
      if (!frameworkKey) return true;
      const awsParsed = parseControlKey(child.mapping.controlKey);
      const fcParsed = parseControlKey(child.mapping.mappedControlKey);
      const awsKey = `${awsParsed.catalog}#${awsParsed.version}`;
      const fcKey = `${fcParsed.catalog}#${fcParsed.version}`;
      return awsKey === frameworkKey || fcKey === frameworkKey;
    };

    const filtered = groupedMappings
      .map(parent => {
        const awsControlParsed = parseControlKey(parent.awsControlKey);
        const awsControlDetails = getControlDetails(
          `${awsControlParsed.catalog}#${awsControlParsed.version}`,
          parent.awsControlKey
        );

        const parentTextMatches = !term || (
          parent.awsControlKey.toLowerCase().includes(term) ||
          awsControlParsed.controlId.toLowerCase().includes(term) ||
          (awsControlDetails?.title?.toLowerCase().includes(term)) ||
          (awsControlDetails?.description?.toLowerCase().includes(term))
        );

        const matchingChildren = parent.children.filter(child => {
          if (!matchesFramework(child)) return false;
          if (!term) return true;
          const fcParsed = parseControlKey(child.mapping.mappedControlKey);
          const fcDetails = getControlDetails(
            `${fcParsed.catalog}#${fcParsed.version}`,
            child.mapping.mappedControlKey
          );
          return (
            child.mapping.mappedControlKey.toLowerCase().includes(term) ||
            child.mapping.mappingKey.toLowerCase().includes(term) ||
            fcParsed.controlId.toLowerCase().includes(term) ||
            (fcDetails?.title?.toLowerCase().includes(term)) ||
            (fcDetails?.description?.toLowerCase().includes(term))
          );
        });

        // Parent with no surviving children is dropped.
        // Text match on the parent alone only counts when a framework filter
        // isn't active, since the filter must apply to at least one child.
        const keep = matchingChildren.length > 0
          || (parentTextMatches && !frameworkKey);
        if (!keep) return null;

        return {
          ...parent,
          children: matchingChildren.length > 0
            ? matchingChildren
            : parent.children,
        };
      })
      .filter(Boolean);

    setFilteredMappings(filtered);

    if (filtered.length > 0) {
      setExpandedRows(prev => {
        const newExpanded = new Set(prev);
        filtered.forEach(parent => {
          if (parent.children.length > 0) {
            newExpanded.add(parent.awsControlKey);
          }
        });
        return newExpanded;
      });
    }
  }, [searchTerm, frameworkFilter, groupedMappings, controlDetails]);

  const groupMappingsByAwsControl = (mappings) => {
    const groups = new Map();
    
    mappings.forEach(item => {
      const awsControlKey = item.mapping.controlKey;
      
      // Skip mappings with missing control keys
      if (!awsControlKey) {
        console.warn('⚠️ Skipping mapping with missing controlKey:', item);
        return;
      }
      
      if (!groups.has(awsControlKey)) {
        groups.set(awsControlKey, {
          awsControlKey,
          isParent: true,
          children: [],
        });
      }
      
      groups.get(awsControlKey).children.push({
        ...item,
        isChild: true,
        parentKey: awsControlKey,
      });
    });
    
    return Array.from(groups.values());
  };

  const loadAvailableFrameworks = async () => {
    try {
      // Load available frameworks from DynamoDB
      const frameworks = await getDataService().getAvailableFrameworks();
      setAvailableFrameworks(frameworks.map(f => ({
        label: `${f.frameworkName} ${f.version}`,
        value: f.frameworkKey,
        frameworkKey: f.frameworkKey,
        frameworkName: f.frameworkName,
        version: f.version,
      })));
    } catch (err) {
      console.error('Error loading frameworks:', err);
    }
  };

  const loadMappings = async () => {
    setLoading(true);
    try {
      const rawData = await getDataService().getAllMappingsWithReviews();
      const data = await filterVisibleMappings(rawData);
      setMappingsData(data);
      
      // Extract unique control keys to fetch details
      const controlsToFetch = [];
      data.forEach(item => {
        const awsControl = parseControlKey(item.mapping.controlKey);
        const frameworkControl = parseControlKey(item.mapping.mappedControlKey);
        
        controlsToFetch.push({
          frameworkKey: `${awsControl.catalog}#${awsControl.version}`,
          controlKey: item.mapping.controlKey,
        });
        
        controlsToFetch.push({
          frameworkKey: `${frameworkControl.catalog}#${frameworkControl.version}`,
          controlKey: item.mapping.mappedControlKey,
        });
      });
      
      const details = await getDataService().batchGetFrameworkControls(controlsToFetch);
      setControlDetails(details);
      
      setError(null);
    } catch (err) {
      console.error('Error loading mappings:', err);
      setError({ 
        type: 'error', 
        message: `Failed to load mappings: ${err.message}` 
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (awsControlKey) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(awsControlKey)) {
      newExpanded.delete(awsControlKey);
    } else {
      newExpanded.add(awsControlKey);
    }
    setExpandedRows(newExpanded);
  };

  const expandAll = () => {
    const allKeys = new Set(groupedMappings.map(g => g.awsControlKey));
    setExpandedRows(allKeys);
  };

  const collapseAll = () => {
    setExpandedRows(new Set());
  };

  const handleReview = (mappingData, decision) => {
    setCurrentMapping(mappingData);
    setReviewDecision(decision);
    setJustification('');
    setShowReviewModal(true);
  };

  const submitReview = async () => {
    if (!justification.trim()) {
      alert('Justification is required');
      return;
    }

    try {
      const userId = await getUserAlias();
      const userName = await getCleanUsername();

      await getDataService().submitReview({
        mappingKey: currentMapping.mapping.mappingKey,
        reviewerId: userId,
        reviewerName: userName,
        decision: reviewDecision,
        justification: justification.trim(),
      });

      setError({ 
        type: 'success', 
        message: `Review ${reviewDecision} submitted successfully!` 
      });
      setTimeout(() => setError(null), 3000);

      await loadMappings();

      setShowReviewModal(false);
      setCurrentMapping(null);
      setJustification('');
    } catch (err) {
      console.error('Error submitting review:', err);
      setError({ 
        type: 'error', 
        message: `Failed to submit review: ${err.message}` 
      });
    }
  };

  const handleFinalSubmission = (mappingData) => {
    setCurrentMapping(mappingData);
    setFinalJustification('');
    setFinalDecision('');
    setIsEdgeCaseMapping(false);
    setShowFinalSubmissionModal(true);
  };

  const submitFinalSubmission = async (decision) => {
    if (!finalJustification.trim()) {
      alert('Justification is required for final submission');
      return;
    }

    try {
      const userId = await getUserAlias();
      const userName = await getCleanUsername();

      // Submit as a review with decision 'final-approve' or 'final-reject' to mapping-reviews table
      await getDataService().submitReview({
        mappingKey: currentMapping.mapping.mappingKey,
        reviewerId: userId,
        reviewerName: userName,
        decision: decision,
        justification: finalJustification.trim(),
        metadata: {
          isEdgeCaseMapping: isEdgeCaseMapping,
          status: 'locked',
        },
      });

      const decisionLabel = decision === 'final-approve' ? 'approved' : 'rejected';
      setError({ 
        type: 'success', 
        message: `Final submission completed successfully! Mapping is now locked and ${decisionLabel}.` 
      });
      setTimeout(() => setError(null), 3000);

      await loadMappings();

      setShowFinalSubmissionModal(false);
      setCurrentMapping(null);
      setFinalJustification('');
      setFinalDecision('');
      setIsEdgeCaseMapping(false);
    } catch (err) {
      console.error('Error submitting final submission:', err);
      setError({ 
        type: 'error', 
        message: `Failed to submit final submission: ${err.message}` 
      });
    }
  };

  const handleSuggestMapping = (awsControlKey) => {
    setCurrentAwsControl(awsControlKey);
    setSelectedFramework(null);
    setSelectedControl(null);
    setSuggestionJustification('');
    setAvailableControls([]);
    setShowSuggestModal(true);
  };

  const handleFrameworkChange = async ({ detail }) => {
    setSelectedFramework(detail.selectedOption);
    setSelectedControl(null);
    
    if (detail.selectedOption) {
      try {
        // Load controls for selected framework using frameworkKey
        const controls = await getDataService().getFrameworkControlsByKey(
          detail.selectedOption.frameworkKey
        );
        
        setAvailableControls(controls.map(c => ({
          label: `${c.controlId} - ${c.title || 'No title'}`,
          value: c.controlKey,
          controlKey: c.controlKey,
          controlId: c.controlId,
          title: c.title,
        })));
      } catch (err) {
        console.error('Error loading framework controls:', err);
        setError({
          type: 'error',
          message: `Failed to load framework controls: ${err.message}`
        });
      }
    }
  };

  const submitSuggestion = async () => {
    if (!selectedControl || !suggestionJustification.trim()) {
      alert('Please select a control and provide justification');
      return;
    }

    try {
      const userId = await getUserAlias();
      const userName = await getCleanUsername();

      await getDataService().suggestMapping({
        controlKey: currentAwsControl,
        mappedControlKey: selectedControl.controlKey,
        reviewerId: userId,
        reviewerName: userName,
        justification: suggestionJustification.trim(),
      });

      setError({
        type: 'success',
        message: 'Mapping suggestion submitted successfully!'
      });
      setTimeout(() => setError(null), 3000);

      await loadMappings();

      setShowSuggestModal(false);
      setCurrentAwsControl(null);
      setSelectedFramework(null);
      setSelectedControl(null);
      setSuggestionJustification('');
    } catch (err) {
      console.error('Error submitting suggestion:', err);
      setError({
        type: 'error',
        message: `Failed to submit suggestion: ${err.message}`
      });
    }
  };

  const parseControlKey = (key) => {
    // Handle undefined or null keys
    if (!key) {
      return {
        full: '',
        catalog: '',
        version: '',
        controlId: '',
      };
    }
    
    const parts = key.split('#');
    return {
      full: key,
      catalog: parts[0] || '',
      version: parts[1] || '',
      controlId: parts.slice(2).join('#') || key,
    };
  };

  const getControlDetails = (frameworkKey, fullControlKey) => {
    const lookupKey = `${frameworkKey}#${fullControlKey}`;
    return controlDetails.get(lookupKey);
  };

  const isValidDocumentationUrl = (url) => {
    return url && url !== 'NOT_MAPPED' && url.startsWith('http');
  };

  const getReviewStatus = (reviews) => {
    if (!reviews || reviews.length === 0) {
      return { type: 'pending', label: 'Not Reviewed' };
    }
    
    const latestReview = reviews[0];
    if (latestReview.decision === 'approve') {
      return { type: 'success', label: 'Approved' };
    } else if (latestReview.decision === 'reject') {
      return { type: 'error', label: 'Rejected' };
    } else if (latestReview.decision === 'final-approve') {
      return { type: 'success', label: 'Final Approved' };
    } else if (latestReview.decision === 'final-reject') {
      return { type: 'error', label: 'Final Rejected' };
    }
    return { type: 'pending', label: 'Pending' };
  };

  const getMappingStatus = (mappingWithReviews) => {
    const { reviews } = mappingWithReviews;
    
    if (!reviews || reviews.length === 0) {
      return 'not-reviewed';
    }

    // Check for final decisions
    const hasFinalApprove = reviews.some(r => r.decision === 'final-approve');
    const hasFinalReject = reviews.some(r => r.decision === 'final-reject');

    if (hasFinalApprove) return 'approved';
    if (hasFinalReject) return 'rejected';

    // Has reviews but no final decision
    return 'in-review';
  };

  const isLocked = (item) => {
    // A mapping is locked if it has a final submission (final-approve or final-reject)
    if (!item.reviews || item.reviews.length === 0) {
      return false;
    }
    const latestReview = item.reviews[0];
    return latestReview.decision === 'final-approve' || latestReview.decision === 'final-reject';
  };

  const getParentStatus = (children) => {
    const allLocked = children.every(child => isLocked(child));
    const anyLocked = children.some(child => isLocked(child));
    
    // Only show "All Approved" or "All Rejected" if all children have final submissions
    if (allLocked) {
      // Check if all final submissions were approvals or rejections
      const allFinalApproved = children.every(child => {
        const latestReview = child.reviews && child.reviews[0];
        return latestReview && latestReview.decision === 'final-approve';
      });
      const allFinalRejected = children.every(child => {
        const latestReview = child.reviews && child.reviews[0];
        return latestReview && latestReview.decision === 'final-reject';
      });
      
      if (allFinalApproved) {
        return { type: 'success', label: 'All Final Approved', color: 'green' };
      } else if (allFinalRejected) {
        return { type: 'error', label: 'All Final Rejected', color: 'red' };
      } else {
        return { type: 'stopped', label: 'All Locked', color: 'grey' };
      }
    }
    
    if (anyLocked) {
      return { type: 'in-progress', label: 'Partially Locked', color: 'grey' };
    }
    
    // If not all locked, status is always "In Review"
    return { type: 'pending', label: 'In Review', color: 'blue' };
  };

  const renderTableItems = () => {
    const items = [];
    
    filteredMappings.forEach(parent => {
      // Add parent row
      items.push(parent);
      
      // Add children if expanded
      if (expandedRows.has(parent.awsControlKey)) {
        items.push(...parent.children);
      }
    });
    
    return items;
  };

  // Calculate pagination
  const allTableItems = renderTableItems();
  const startIndex = (currentPageIndex - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = allTableItems.slice(startIndex, endIndex);
  const totalPages = Math.ceil(allTableItems.length / itemsPerPage);

  if (loading) {
    return (
      <Container>
          <Box textAlign="center" padding="xl">
            <div className="loading-spinner"></div>
            <Box variant="p" margin={{ top: 'm' }}>
              Loading mappings...
            </Box>
          </Box>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h1"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={expandAll} iconName="angle-down">
                  Expand All
                </Button>
                <Button onClick={collapseAll} iconName="angle-up">
                  Collapse All
                </Button>
                <Button onClick={loadMappings} iconName="refresh">
                  Refresh
                </Button>
              </SpaceBetween>
            }
          >
            Control Mappings Review
          </Header>
        }
      >
        {error && (
          <Alert 
            type={error.type || 'error'} 
            dismissible 
            onDismiss={() => setError(null)}
          >
            {error.message}
          </Alert>
        )}

        <SpaceBetween size="m">
          <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
            <Input
              value={searchTerm}
              onChange={({ detail }) => setSearchTerm(detail.value)}
              placeholder="Search by AWS control or framework control..."
              type="search"
              clearAriaLabel="Clear search"
            />
            <Select
              selectedOption={frameworkFilter ?? { label: 'All frameworks', value: 'all' }}
              onChange={({ detail }) => setFrameworkFilter(
                detail.selectedOption.value === 'all' ? null : detail.selectedOption
              )}
              options={[
                { label: 'All frameworks', value: 'all' },
                ...availableFrameworks,
              ]}
              placeholder="Filter by framework"
              filteringType="auto"
              empty="No frameworks available"
            />
          </Grid>

          <Table
            items={paginatedItems}
            pagination={
              <Pagination
                currentPageIndex={currentPageIndex}
                onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
                pagesCount={totalPages}
              />
            }
            columnDefinitions={[
              {
                id: 'control',
                header: 'Control',
                width: 500,
                cell: item => {
                  if (item.isParent) {
                    // Parent row - AWS Control
                    const parsed = parseControlKey(item.awsControlKey);
                    const details = getControlDetails(
                      `${parsed.catalog}#${parsed.version}`,
                      item.awsControlKey
                    );
                    const isExpanded = expandedRows.has(item.awsControlKey);
                    
                    // Parse additionalInfo - it's already unmarshalled into a plain JavaScript array
                    const additionalInfo = Array.isArray(details?.additionalInfo) 
                      ? details.additionalInfo 
                      : [];
                    const hasReferences = additionalInfo.length > 0;
                    
                    return (
                      <SpaceBetween size="xs">
                        <Box>
                          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                            <Button
                              variant="icon"
                              iconName={isExpanded ? 'angle-down' : 'angle-right'}
                              onClick={() => toggleRow(item.awsControlKey)}
                            />
                            {isValidDocumentationUrl(details?.documentationUrl) ? (
                              <Link
                                external
                                href={details.documentationUrl}
                                target="_blank"
                                variant="primary"
                                fontSize="heading-m"
                              >
                                {parsed.controlId}
                              </Link>
                            ) : (
                              <Box variant="h3">{parsed.controlId}</Box>
                            )}
                            <Badge color="blue">{item.children.length} mapping(s)</Badge>
                          </SpaceBetween>
                        </Box>
                        {details && details.title && (
                          <Box variant="p" margin={{ left: 'xxl' }}>
                            {details.title}
                          </Box>
                        )}
                        {details && details.description && (
                          <Box variant="small" color="text-body-secondary" margin={{ left: 'xxl' }}>
                            {details.description}
                          </Box>
                        )}
                        {hasReferences && (
                          <Box margin={{ left: 'xxl', top: 's' }}>
                            <ExpandableSection
                              headerText="References"
                              variant="footer"
                              defaultExpanded={false}
                            >
                              <SpaceBetween size="m">
                                {additionalInfo.map((info, index) => {
                                  // Data is already unmarshalled - access properties directly
                                  const awsArn = info.AwsArn;
                                  const implementation = info.Implementation;
                                  const aliases = info.Aliases || [];
                                  const governedResources = info.GovernedResources || [];
                                  const severity = info.Severity;
                                  const behavior = info.Behavior;
                                  const createTime = info.CreateTime;
                                  
                                  return (
                                    <Box key={index} padding="s">
                                      <SpaceBetween size="xs">
                                        {awsArn && (
                                          <Box>
                                            <Box variant="strong">AWS ARN:</Box>
                                            <Box variant="code">{awsArn}</Box>
                                          </Box>
                                        )}
                                        {implementation && (
                                          <Box>
                                            <Box variant="strong">Implementation:</Box>
                                            <Box variant="small">
                                              Type: {implementation.Type || 'N/A'}, 
                                              Identifier: {implementation.Identifier || 'N/A'}
                                            </Box>
                                          </Box>
                                        )}
                                        {aliases.length > 0 && (
                                          <Box>
                                            <Box variant="strong">Aliases:</Box>
                                            <Box variant="small">
                                              {aliases.join(', ')}
                                            </Box>
                                          </Box>
                                        )}
                                        {governedResources.length > 0 && (
                                          <Box>
                                            <Box variant="strong">Governed Resources:</Box>
                                            <Box variant="small">
                                              {governedResources.join(', ')}
                                            </Box>
                                          </Box>
                                        )}
                                        {severity && (
                                          <Box>
                                            <Box variant="strong">Severity:</Box>
                                            <Badge color={severity === 'HIGH' ? 'red' : severity === 'MEDIUM' ? 'blue' : 'grey'}>
                                              {severity}
                                            </Badge>
                                          </Box>
                                        )}
                                        {behavior && (
                                          <Box>
                                            <Box variant="strong">Behavior:</Box>
                                            <Box variant="small">{behavior}</Box>
                                          </Box>
                                        )}
                                        {createTime && (
                                          <Box>
                                            <Box variant="strong">Created:</Box>
                                            <Box variant="small">{new Date(createTime).toLocaleDateString()}</Box>
                                          </Box>
                                        )}
                                      </SpaceBetween>
                                    </Box>
                                  );
                                })}
                              </SpaceBetween>
                            </ExpandableSection>
                          </Box>
                        )}
                      </SpaceBetween>
                    );
                  } else {
                    // Child row - Framework Control
                    const parsed = parseControlKey(item.mapping.mappedControlKey);
                    const details = getControlDetails(
                      `${parsed.catalog}#${parsed.version}`,
                      item.mapping.mappedControlKey
                    );
                    
                    // Check if this mapping was user-suggested using the isSuggested flag
                    const isUserSuggested = item.mapping.isSuggested === true;
                    
                    return (
                      <Box margin={{ left: 'xxl' }}>
                        <SpaceBetween size="xs">
                          <SpaceBetween direction="horizontal" size="xs">
                            <Badge color="blue">{parsed.catalog} {parsed.version}</Badge>
                            {isUserSuggested && (
                              <Badge>User Suggested</Badge>
                            )}
                            <Box variant="strong">{parsed.controlId}</Box>
                          </SpaceBetween>
                          {details && details.title && (
                            <Box variant="p">{details.title}</Box>
                          )}
                          {details && details.description && (
                            <Box variant="small" color="text-body-secondary">
                              {details.description}
                            </Box>
                          )}
                        </SpaceBetween>
                      </Box>
                    );
                  }
                },
              },
              {
                id: 'status',
                header: 'Status',
                width: 300,
                cell: item => {
                  if (item.isParent) {
                    const parentStatus = getParentStatus(item.children);
                    return (
                      <SpaceBetween size="xs">
                        <StatusIndicator type={parentStatus.type}>
                          {parentStatus.label}
                        </StatusIndicator>
                      </SpaceBetween>
                    );
                  } else {
                    const status = getReviewStatus(item.reviews);
                    const itemIsLocked = isLocked(item);
                    
                    return (
                      <SpaceBetween size="xs">
                        {itemIsLocked ? (
                          <>
                            {/* Show final decision prominently */}
                            {item.reviews && item.reviews[0] && (
                              <>
                                <StatusIndicator 
                                  type={item.reviews[0].decision === 'final-approve' ? 'success' : 'error'}
                                >
                                  {item.reviews[0].decision === 'final-approve' ? 'Final Approved' : 'Final Rejected'}
                                </StatusIndicator>
                                <Box variant="small" color="text-body-secondary">
                                  <SpaceBetween size="xxs">
                                    <Box>
                                      <strong>{item.reviews[0].reviewerName}</strong>
                                    </Box>
                                    <Box>
                                      {new Date(item.reviews[0].submittedAt).toLocaleDateString()}
                                    </Box>
                                    {item.reviews[0].justification && (
                                      <Box fontSize="body-s" fontStyle="italic">
                                        "{item.reviews[0].justification}"
                                      </Box>
                                    )}
                                  </SpaceBetween>
                                </Box>
                              </>
                            )}
                            <Badge color="grey">🔒 Locked</Badge>
                            
                            {/* Show complete review history in expandable section */}
                            {item.reviews && item.reviews.length > 1 && (
                              <Box margin={{ top: 's' }}>
                                <ExpandableSection
                                  headerText={`Review History (${item.reviews.length - 1} previous review${item.reviews.length - 1 !== 1 ? 's' : ''})`}
                                  variant="footer"
                                  defaultExpanded={false}
                                >
                                  <SpaceBetween size="s">
                                    {item.reviews.slice(1).map((review, index) => (
                                      <Box key={index} padding="s">
                                        <SpaceBetween size="xxs">
                                          <SpaceBetween direction="horizontal" size="xs">
                                            <Badge color={review.decision === 'approve' ? 'green' : review.decision === 'reject' ? 'red' : 'blue'}>
                                              {review.decision}
                                            </Badge>
                                            <Box variant="strong">{review.reviewerName}</Box>
                                          </SpaceBetween>
                                          <Box variant="small" color="text-body-secondary">
                                            {new Date(review.submittedAt).toLocaleString()}
                                          </Box>
                                          {review.justification && (
                                            <Box fontSize="body-s" fontStyle="italic">
                                              "{review.justification}"
                                            </Box>
                                          )}
                                        </SpaceBetween>
                                      </Box>
                                    ))}
                                  </SpaceBetween>
                                </ExpandableSection>
                              </Box>
                            )}
                          </>
                        ) : (
                          <>
                            <StatusIndicator type={status.type}>
                              {status.label}
                            </StatusIndicator>
                            {item.reviews && item.reviews.length > 0 && (
                              <>
                                <Box variant="small" color="text-body-secondary">
                                  {item.reviews.length} review(s)
                                </Box>
                                {item.reviews.slice(0, 2).map((review, index) => (
                                  <Box key={index} variant="small" color="text-body-secondary">
                                    <SpaceBetween size="xxs">
                                      <Box>
                                        <strong>{review.reviewerName}</strong> - {review.decision}
                                      </Box>
                                      {review.justification && (
                                        <Box fontSize="body-s" fontStyle="italic">
                                          "{review.justification}"
                                        </Box>
                                      )}
                                    </SpaceBetween>
                                  </Box>
                                ))}
                                {item.reviews.length > 2 && (
                                  <Box variant="small" color="text-body-secondary">
                                    +{item.reviews.length - 2} more review(s)
                                  </Box>
                                )}
                              </>
                            )}
                          </>
                        )}
                      </SpaceBetween>
                    );
                  }
                },
              },
              {
                id: 'actions',
                header: 'Actions',
                width: 300,
                cell: item => {
                  if (item.isParent) {
                    return (
                      <Button
                        iconName="add-plus"
                        onClick={() => handleSuggestMapping(item.awsControlKey)}
                      >
                        Suggest Mapping
                      </Button>
                    );
                  } else {
                    const hasReviews = item.reviews && item.reviews.length > 0;
                    const itemIsLocked = isLocked(item);
                    
                    return (
                      <SpaceBetween direction="vertical" size="xs">
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button
                            iconName="thumbs-up-filled"
                            onClick={() => handleReview(item, 'approve')}
                            disabled={itemIsLocked}
                          >
                            Approve
                          </Button>
                          <Button
                            iconName="thumbs-down-filled"
                            onClick={() => handleReview(item, 'reject')}
                            disabled={itemIsLocked}
                          >
                            Reject
                          </Button>
                        </SpaceBetween>
                        {hasReviews && !itemIsLocked && (
                          <Button
                            variant="primary"
                            iconName="status-positive"
                            onClick={() => handleFinalSubmission(item)}
                            size="small"
                          >
                            Final Submission
                          </Button>
                        )}
                        {itemIsLocked && (
                          <Badge color="grey" size="small">
                            🔒 Locked
                          </Badge>
                        )}
                      </SpaceBetween>
                    );
                  }
                },
              },
            ]}
            variant="container"
            stickyHeader
            wrapLines
            empty={
              <Box textAlign="center" padding="l">
                <SpaceBetween size="m">
                  <Box variant="p" color="text-body-secondary">
                    No mappings found
                  </Box>
                  {searchTerm && (
                    <Button onClick={() => setSearchTerm('')}>
                      Clear search
                    </Button>
                  )}
                </SpaceBetween>
              </Box>
            }
            header={
              <Header
                counter={`(${filteredMappings.length} AWS Control${filteredMappings.length !== 1 ? 's' : ''})`}
              >
                Grouped Control Mappings
              </Header>
            }
          />
        </SpaceBetween>
      </Container>

      {/* Review Modal */}
      <Modal
        onDismiss={() => setShowReviewModal(false)}
        visible={showReviewModal}
        closeAriaLabel="Close modal"
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowReviewModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submitReview}
                disabled={!justification.trim()}
              >
                Submit {reviewDecision}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={`${reviewDecision === 'approve' ? 'Approve' : 'Reject'} Mapping`}
      >
        {currentMapping && (
          <SpaceBetween size="m">
            <Box>
              <Box variant="h3">Mapping Details</Box>
              <SpaceBetween size="xs">
                <Box>
                  <Box variant="strong">AWS Control:</Box>
                  <Box>{parseControlKey(currentMapping.mapping.controlKey).controlId}</Box>
                </Box>
                <Box>
                  <Box variant="strong">Framework Control:</Box>
                  <Box>{parseControlKey(currentMapping.mapping.mappedControlKey).controlId}</Box>
                </Box>
                <Box>
                  <Box variant="strong">Mapping Key:</Box>
                  <Box variant="small" color="text-body-secondary">
                    {currentMapping.mapping.mappingKey}
                  </Box>
                </Box>
              </SpaceBetween>
            </Box>

            {currentMapping.reviews && currentMapping.reviews.length > 0 && (
              <Box>
                <Box variant="h3">Review History ({currentMapping.reviews.length})</Box>
                <SpaceBetween size="xs">
                  {currentMapping.reviews.map((review, index) => (
                    <Box key={index} variant="small" color="text-body-secondary">
                      <SpaceBetween direction="horizontal" size="xs">
                        <Box>{new Date(review.submittedAt).toLocaleDateString()}</Box>
                        <Box>-</Box>
                        <Box>{review.decision}</Box>
                        <Box>by {review.reviewerName}</Box>
                      </SpaceBetween>
                      {review.justification && (
                        <Box margin={{ top: 'xs' }}>
                          "{review.justification}"
                        </Box>
                      )}
                    </Box>
                  ))}
                </SpaceBetween>
              </Box>
            )}

            <FormField
              label="Justification"
              description={`Explain why you are ${reviewDecision === 'approve' ? 'approving' : 'rejecting'} this mapping.`}
            >
              <Textarea
                value={justification}
                onChange={({ detail }) => setJustification(detail.value)}
                placeholder="Enter your justification..."
                rows={4}
              />
            </FormField>
          </SpaceBetween>
        )}
      </Modal>

      {/* Final Submission Modal */}
      <Modal
        onDismiss={() => setShowFinalSubmissionModal(false)}
        visible={showFinalSubmissionModal}
        closeAriaLabel="Close modal"
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowFinalSubmissionModal(false)}>
                Cancel
              </Button>
              <Button
                iconName="thumbs-down-filled"
                onClick={() => submitFinalSubmission('final-reject')}
                disabled={!finalJustification.trim()}
              >
                Final Reject
              </Button>
              <Button
                variant="primary"
                iconName="thumbs-up-filled"
                onClick={() => submitFinalSubmission('final-approve')}
                disabled={!finalJustification.trim()}
              >
                Final Approve
              </Button>
            </SpaceBetween>
          </Box>
        }
        header="Final Submission"
      >
        {currentMapping && (
          <SpaceBetween size="l">
            <Box>
              <Box variant="p" color="text-body-secondary">
                You are about to submit your final review for this mapping. 
                This will lock the mapping and prevent further changes.
              </Box>
            </Box>

            <Box>
              <Box variant="h3">Mapping Details</Box>
              <SpaceBetween size="xs">
                <Box>
                  <Box variant="strong">AWS Control:</Box>
                  <Box>{parseControlKey(currentMapping.mapping.controlKey).controlId}</Box>
                </Box>
                <Box>
                  <Box variant="strong">Framework Control:</Box>
                  <Box>{parseControlKey(currentMapping.mapping.mappedControlKey).controlId}</Box>
                </Box>
              </SpaceBetween>
            </Box>
            
            <FormField
              label="Final Justification"
              description="Provide an overall justification for your review decision."
              errorText={!finalJustification.trim() ? "Justification is required for final submission" : ""}
            >
              <Textarea
                value={finalJustification}
                onChange={({ detail }) => setFinalJustification(detail.value)}
                placeholder="Enter your overall justification for the review decision..."
                rows={4}
              />
            </FormField>

            <FormField
              label="Additional Classifications"
              description="Select any additional classifications that apply to this review."
            >
              <Checkbox
                checked={isEdgeCaseMapping}
                onChange={({ detail }) => setIsEdgeCaseMapping(detail.checked)}
              >
                Edge Case Mapping
              </Checkbox>
            </FormField>
          </SpaceBetween>
        )}
      </Modal>

      {/* Suggest Mapping Modal */}
      <Modal
        onDismiss={() => setShowSuggestModal(false)}
        visible={showSuggestModal}
        closeAriaLabel="Close modal"
        size="large"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowSuggestModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submitSuggestion}
                disabled={!selectedControl || !suggestionJustification.trim()}
              >
                Submit Suggestion
              </Button>
            </SpaceBetween>
          </Box>
        }
        header="Suggest New Mapping"
      >
        {currentAwsControl && (
          <SpaceBetween size="l">
            <Box>
              <Box variant="p" color="text-body-secondary">
                Suggest a new framework control mapping for this AWS control.
              </Box>
            </Box>

            <Box>
              <Box variant="h3">AWS Control</Box>
              <SpaceBetween size="xs">
                <Badge color="green">AWS</Badge>
                <Box variant="strong">
                  {parseControlKey(currentAwsControl).controlId}
                </Box>
              </SpaceBetween>
            </Box>

            <FormField
              label="Framework"
              description="Select the framework to map to"
            >
              <Select
                selectedOption={selectedFramework}
                onChange={handleFrameworkChange}
                options={availableFrameworks}
                placeholder="Choose a framework"
                filteringType="auto"
              />
            </FormField>

            {selectedFramework && (
              <FormField
                label="Framework Control"
                description="Select the specific control to map to"
              >
                <Select
                  selectedOption={selectedControl}
                  onChange={({ detail }) => setSelectedControl(detail.selectedOption)}
                  options={availableControls}
                  placeholder="Choose a control"
                  filteringType="auto"
                />
              </FormField>
            )}

            <FormField
              label="Justification"
              description="Explain why this mapping should be added"
            >
              <Textarea
                value={suggestionJustification}
                onChange={({ detail }) => setSuggestionJustification(detail.value)}
                placeholder="Enter your justification for this mapping suggestion..."
                rows={4}
              />
            </FormField>
          </SpaceBetween>
        )}
      </Modal>
    </SpaceBetween>
  );
};

export default MappingReviewTable;
